import * as ROUTES from '@/shared/constants/routes';
import { useState, useCallback, useMemo, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { passwordResetService } from '@/features/auth/services/passwordResetService';
import { useSpinner } from '@chikara/ui';
import { t } from '@/i18n';

const PASSWORD_MIN_LENGTH = 8;

export const useResetPassword = (onSuccess?: () => void) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { show, hide, isSpinnerLoading } = useSpinner();

  const token = searchParams.get('reference');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [apiError, setApiError] = useState<string | null>(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);

  // Password validation rules
  const isLengthValid = password.length >= PASSWORD_MIN_LENGTH;
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);

  const isValidPassword = isLengthValid && hasNumber && hasSpecial && hasUppercase;
  const isMatchValid = password.length > 0 && password === confirmPassword;
  const isFormValid = isValidPassword && isMatchValid && !isSpinnerLoading;

  const passwordError = useMemo(() => {
    if (confirmPassword.length > 0 && !isMatchValid) {
      return t('resetPassword.passwordMismatch');
    }
    return undefined;
  }, [confirmPassword, isMatchValid]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (apiError) setApiError(null);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (apiError) setApiError(null);
  };

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!isFormValid || !token) return;

    setApiError(null);
    show();

    try {
      await passwordResetService.confirmPasswordReset({
        token,
        new_password: password,
      });

      if (onSuccess) {
        onSuccess();
      } else {
        navigate(ROUTES.LOGIN, {
          state: { toast: t('resetPassword.success') },
        });
      }
    } catch (err: any) {
      const status = err?.status || err?.response?.status;
      if (status === 400 || status === 410) {
        setIsTokenExpired(true);
      } else {
        setApiError(t('resetPassword.error'));
      }
    } finally {
      hide();
    }
  }, [isFormValid, token, password, show, hide, navigate, onSuccess]);

  const goToForgotPassword = () => {
    navigate(ROUTES.FORGOT_PASSWORD);
  };

  return {
    password,
    confirmPassword,
    passwordError,
    apiError,
    isLengthValid,
    hasNumber,
    hasSpecial,
    hasUppercase,
    isFormValid,
    isTokenExpired,
    isLoading: isSpinnerLoading,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleSubmit,
    goToForgotPassword,
  };
};
